import { computed, onUnmounted, readonly, ref, watch } from 'vue'

type OptionRotateKeyTransition<D> = Partial<{
  default?: D,
  overrun?: boolean
}>

export const useRotateKeyTransition = <const K extends Array<any>>(keys: K, opt: OptionRotateKeyTransition<K[number]> = {}) => {
  const key = ref(0)
  const animation = ref('slide-left')
  const overrun = opt.overrun ?? true

  if (opt.default !== undefined) {
    const index = keys.indexOf(opt.default as K[number])
    index !== -1 && (key.value = index)
  }

  const hasNext = computed(() => key.value < keys.length - 1)
  const hasPrev = computed(() => key.value > 0)

  const next = () => {
    if (overrun) {
      return key.value = (key.value + 1) % keys.length
    }

    return key.value = hasNext.value ? key.value + 1 : key.value
  }

  const prev = () => {
    if (overrun) {
      return key.value = (key.value - 1 + keys.length) % keys.length
    }

    return key.value = hasPrev.value ? key.value - 1 : 0
  }

  const current = computed(() => keys[key.value] as K[number])

  const stop = watch(key, (value, old) => {
    animation.value = value > old ? 'slide-left' : 'slide-right'
  })

  const setIndex = (index: number) => {
    if (index >= 0 && index < keys.length) {
      key.value = index
    }
  }

  const set = (value: K[number]) => {
    const index = keys.indexOf(value)
    if (index !== -1) {
      key.value = index
    }
  }

  const is = (value: K[number]) => {
    return current.value === value
  }

  onUnmounted(stop)

  return {
    index: readonly(key),
    current,
    next,
    prev,
    set,
    is,
    setIndex,
    animation,
    hasNext,
    hasPrev,
    keys: readonly(keys)
  }
}